import { ResourceType } from '@cotera/api';
import { LlmUtils } from '@cotera/utilities';
import type { ContentNode, DocumentState } from './document-types';
import type { RenderContext } from './handlers/types';
import { agentIdToAgentToolKey } from '../../chat/coco-tools';
import type { WorkspaceRenderBundle } from './workspace-render-bundle';

const WORKSPACE_INPUT_PRIMITIVE_TYPES = new Set([
  'string',
  'number',
  'boolean',
  'super',
]);

/**
 * Renders a Coco `t: "input"` mention as inline markdown for workspace / LLM prompts:
 * `` `{{input:<key>}}` (type) ``. Returns null for construct placeholders or unparsable attrs.
 */
export function formatWorkspaceInputMentionMarkdown(
  attrs: Record<string, unknown> | undefined
): string | null {
  if (attrs?.['data'] === undefined) {
    return null;
  }
  try {
    const data =
      typeof attrs['data'] === 'string'
        ? JSON.parse(attrs['data'] as string)
        : attrs['data'];
    if (data?.t !== 'input') {
      return null;
    }
    const inner = data?.data as Record<string, unknown> | undefined;
    if (typeof inner !== 'object' || inner === null) {
      return null;
    }
    if (inner['kind'] === 'construct') {
      return null;
    }
    const inputKey = inner['inputKey'];
    const inputType = inner['inputType'];
    if (typeof inputKey !== 'string' || typeof inputType !== 'string') {
      return null;
    }
    if (!WORKSPACE_INPUT_PRIMITIVE_TYPES.has(inputType)) {
      return null;
    }
    return `\`{{input:${inputKey}}}\` (${inputType})`;
  } catch {
    return null;
  }
}

function formatDocumentReference(
  displayText: string,
  resourceId: string
): string {
  return `@${displayText} (document: ${resourceId})`;
}

export type DatasetMentionColumn = {
  readonly name: string;
  readonly type: string | null;
};

/**
 * How a `@`-mentioned dataset appears in a prompt: what it is, how to address it,
 * and what shape it is. Rows are deliberately absent — inlining them would blow up
 * every prompt that names a dataset, and `coco/dataset-read` (granted by the same
 * mention) pages through them on demand.
 *
 * `columns` empty means the dataset has no artifact yet — a CSV still importing, or
 * a manual dataset whose first write has not landed — which is different from a
 * dataset with no columns, so it says so rather than printing an empty list.
 */
export function formatDatasetMentionMarkdown(params: {
  readonly name: string;
  readonly id: string;
  readonly columns: readonly DatasetMentionColumn[];
}): string {
  const header = `Dataset "${params.name}" (dataset: ${params.id})`;
  if (params.columns.length === 0) {
    return `${header}\nColumns: not available yet (the dataset is still importing).`;
  }
  const lines = params.columns.map(
    (column) => `- ${column.name} (${column.type ?? 'unknown'})`
  );
  return `${header}\nColumns:\n${lines.join('\n')}`;
}

/**
 * When {@link RenderContext.workspace} is set, handles workspace resource mentions
 * without using {@link RenderContext.graph}. Returns `null` to fall back to graph-based logic.
 */
export function tryRenderWorkspaceMention(
  node: ContentNode | DocumentState,
  displayText: string,
  mentionType: string | undefined,
  attrs: Record<string, unknown> | undefined,
  ctx: RenderContext
): string | null {
  const workspace = ctx.workspace;
  if (workspace === undefined) {
    return null;
  }

  if (mentionType === 'input') {
    const formatted = formatWorkspaceInputMentionMarkdown(attrs);
    if (formatted !== null) {
      return formatted;
    }
    return `@${displayText}`;
  }

  if (mentionType === 'tool') {
    return null;
  }

  let resourceType: ResourceType | undefined;
  let resourceId: string | undefined;
  if (attrs?.['data'] !== undefined) {
    try {
      const data =
        typeof attrs['data'] === 'string'
          ? JSON.parse(attrs['data'] as string)
          : attrs['data'];
      const inner = (data as { data?: unknown })?.data;
      if (
        typeof inner === 'object' &&
        inner !== null &&
        'resourceType' in inner &&
        'resourceId' in inner
      ) {
        const rt = String((inner as { resourceType: string }).resourceType);
        const id = String((inner as { resourceId: string }).resourceId);
        if ((Object.values(ResourceType) as string[]).includes(rt)) {
          resourceType = rt as ResourceType;
          resourceId = id;
        }
      }
    } catch {
      // fall through
    }
  }

  if (mentionType === 'agent' && typeof attrs?.['id'] === 'string') {
    resourceType = ResourceType.Agent;
    resourceId = attrs['id'] as string;
  }

  if (resourceType === undefined || resourceId === undefined) {
    return null;
  }

  return renderResolvedResource(
    workspace,
    resourceType,
    resourceId,
    displayText
  );
}

function renderResolvedResource(
  workspace: WorkspaceRenderBundle,
  resourceType: ResourceType,
  resourceId: string,
  displayText: string
): string {
  switch (resourceType) {
    case ResourceType.Agent: {
      const line = workspace.agents.get(resourceId);
      if (line === undefined) {
        return `@${displayText}`;
      }
      return line;
    }
    case ResourceType.Document: {
      const doc = workspace.documents.get(resourceId);
      if (doc === undefined) {
        return formatDocumentReference(displayText, resourceId);
      }
      const title = doc.title.trim();
      if (title.length > 0 && title !== displayText) {
        return formatDocumentReference(title, resourceId);
      }
      return formatDocumentReference(displayText, resourceId);
    }
    case ResourceType.Folder: {
      const structure = workspace.folders.get(resourceId);
      if (structure === undefined) {
        return `@${displayText}`;
      }
      return `Folder "${displayText}" (id: ${resourceId}):\n${structure}`;
    }
    case ResourceType.Dataset: {
      const declaration = workspace.datasets.get(resourceId);
      if (declaration === undefined) {
        return `@${displayText} (dataset: ${resourceId})`;
      }
      return declaration;
    }
    default:
      return `@${displayText} (${resourceType}: ${resourceId})`;
  }
}

/** Builds the same inline tool-style line used for tool mentions, for an agent tool key. */
export function formatAgentResourceAsToolLine(agentId: string): string {
  const key = agentIdToAgentToolKey(agentId);
  const formatted = LlmUtils.formatToolName(key);
  return `\\\`${formatted}\\\` (tool)`;
}
